import { Checkbox, Input, Scope, useFormApi } from 'informed';
import { useState, useRef, useEffect } from 'react';
import TymberInfoBox from '../TymberInfoBox';
import moment from 'moment';
import {
  calculateDurationFromWorkTime,
  calculateWorkTime,
} from '../../../../utils/time';
import { PrimaryButton } from '@tymbe/ui-primitives';
import { ContentIcon, FavouriteIcon } from '@tymbe/ui-icons';
import DropDownMenu from '../ShiftDetail/DropDownMenu';
import { AttendanceResolution } from '@tymbe/schema/enums';
import classNames from 'classnames';
import { TyRadioGroup } from '@tymbe/ty-components/inputs/radio';
import { ThumbRadio } from '../ThumbRadio';
import { TyInput, timeFormatter } from '@tymbe/ui-forms';
import {
  BAD_JOB_EVALUATION_SCORE,
  DEFAULT_JOB_EVALUATION_SCORE,
  GOOD_JOB_EVALUATION_SCORE,
} from '../../../../utils/constants';
import { ApplicationData } from '../../../../types/TymbeApi';

interface UnapprovedAttendanceRowProps {
  application: ApplicationData;
  submitSingle: (key: number) => Promise<void>;
  startDate: string;
  endDate: string;
}

const UnapprovedAttendanceRow = ({
  application,
  submitSingle,
  startDate,
  endDate,
}: UnapprovedAttendanceRowProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const formApi = useFormApi();
  const workedTimeFromStartEnd = (start: string, end: string) => {
    const startMoment = moment(start, 'H:mm');
    const endMoment = moment(end, 'H:mm');

    // If end is before or equal to start, assume it's the next day
    if (endMoment.isSameOrBefore(startMoment)) {
      endMoment.add(1, 'day');
    }
    return moment.duration(endMoment.diff(startMoment)).asHours();
  };

  const initialTotalTime = workedTimeFromStartEnd(
    moment(startDate).format('H:mm'),
    moment(endDate).format('H:mm')
  );
  const initialWorkedTime = calculateWorkTime(
    startDate,
    endDate,
    false,
    'hour'
  );

  const endFromStartAndWorkedTime = (start: string, workedTime: number) => {
    return moment(start, 'H:mm').add(workedTime, 'hours').format('H:mm');
  };

  const handleTimeChange = () => {
    const start = formApi.getValue(`applications.${application.id}.start`);
    const end = formApi.getValue(`applications.${application.id}.end`);
    const startMoment = moment(start, 'H:mm');
    const endMoment = moment(end, 'H:mm');

    if (endMoment.isSameOrBefore(startMoment)) {
      endMoment.add(1, 'day');
    }

    if (startMoment && endMoment) {
      const workedTime = calculateWorkTime(
        startMoment,
        endMoment,
        false,
        'hour'
      );
      const totalTime = workedTimeFromStartEnd(start, end);
      const breakTime = totalTime - workedTime;
      const oldWorkedTime = formApi.getValue(
        `applications.${application.id}.confirmed_time`
      );

      if (
        workedTime === oldWorkedTime ||
        formApi.getFocused(`applications.${application.id}.confirmed_time`)
      )
        return;

      formApi.setValue(
        `applications.${application.id}.confirmed_time`,
        workedTime
      );
      formApi.setValue(`applications.${application.id}.break`, breakTime);
    }
  };

  const handleWorkedTimeChange = () => {
    const start = formApi.getValue(`applications.${application.id}.start`);
    const workedTime = formApi.getValue(
      `applications.${application.id}.confirmed_time`
    );

    if (start && workedTime != null) {
      const totalWorkTime = calculateDurationFromWorkTime(
        workedTime * 3600000,
        false,
        'hours'
      );

      const newEnd = endFromStartAndWorkedTime(start, totalWorkTime);
      const breakTime = totalWorkTime - workedTime;
      const oldEnd = formApi.getValue(`applications.${application.id}.end`);

      if (
        oldEnd === newEnd ||
        formApi.getFocused(`applications.${application.id}.end`)
      )
        return;

      formApi.setValue(`applications.${application.id}.end`, newEnd);
      formApi.setValue(`applications.${application.id}.break`, breakTime);
    }
  };

  const handleAttResolution = (resolution: AttendanceResolution) => {
    formApi.setValue(`applications.${application.id}.resolution`, resolution);
    submitSingle(application.id);
  };

  const menuOptions = [
    {
      text: 'Omluvená absence',
      value: AttendanceResolution.EXCUSED_ABSENCE,
      onClick: () => handleAttResolution(AttendanceResolution.EXCUSED_ABSENCE),
    },
    {
      text: 'Neomluvená absence',
      value: AttendanceResolution.UNEXCUSED_ABSENCE,
      onClick: () =>
        handleAttResolution(AttendanceResolution.UNEXCUSED_ABSENCE),
    },
    {
      text: 'Storno',
      value: AttendanceResolution.CANCEL,
      onClick: () => handleAttResolution(AttendanceResolution.CANCEL),
    },
  ];

  const handleButtonClick = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        x: rect.right,
        y: rect.top
      });
    }
    setOpenMenu(!openMenu);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setOpenMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Scope scope={`applications.${application.id}`}>
      <div className="tymber-info col-start-1 col-span-1 flex items-center gap-2">
        <Input name="id" required type="hidden" initialValue={application.id} />
        <Input
          name="resolution"
          required
          type="hidden"
          initialValue={AttendanceResolution.OK}
        />
        <Checkbox className="size-[18px] rounded" name="selected" initialValue={true} />
        <TymberInfoBox personId={application.person_id} isCompanyJunior={application.isCompanyJunior} />
      </div>

      <div className="confirmed-time col-start-2 col-span-1 row-span-1 my-auto">
        <TyInput
          min="0"
          type="number"
          step={0.01}
          mask={(value: string) => Number(value).toFixed(2)}
          lang="en"
          className="w-[90px]"
          name="confirmed_time"
          suffix="h"
          onChange={handleWorkedTimeChange}
          initialValue={initialWorkedTime}
          maskOnBlur
        />
      </div>

      <div className="break col-start-3 col-span-1 row-span-1 my-auto">
        <TyInput
          min="0"
          type="number"
          step={0.01}
          mask={(value: string) => Number(value).toFixed(2)}
          lang="en"
          className="w-[75px]"
          name="break"
          suffix="h"
          initialValue={initialTotalTime - initialWorkedTime}
          disabled
        />
      </div>

      <div className="start-end col-start-4 col-span-1 row-span-1 flex items-center gap-2">
        <TyInput
          className="w-16"
          name="start"
          formatter={timeFormatter}
          onChange={handleTimeChange}
          initialValue={moment(startDate).format('H:mm')}
        />
        <span>-</span>
        <TyInput
          className="w-16"
          name="end"
          formatter={timeFormatter}
          onChange={handleTimeChange}
          initialValue={moment(endDate).format('H:mm')}
        />
      </div>

      <div className="actions col-start-5 col-span-1 row-span-1 flex justify-end items-center gap-2 pl-7">
        <TyRadioGroup
          name="rating"
          defaultValue={`${DEFAULT_JOB_EVALUATION_SCORE}`}
          className="flex items-center gap-2"
        >
          <ThumbRadio
            variant="down"
            className="size-[30px]"
            size={25}
            value={`${BAD_JOB_EVALUATION_SCORE}`}
          />
          <ThumbRadio
            variant="up"
            className="size-[30px]"
            size={25}
            value={`${GOOD_JOB_EVALUATION_SCORE}`}
          />
        </TyRadioGroup>

        <PrimaryButton
          className="flex gap-2 items-center h-10"
          onClick={() => {
            submitSingle(application.id);
          }}
        >
          <FavouriteIcon
            name="check"
            className="size-[18px] min-h-[18px] min-w-[18px]"
          />
          Potvrdit
        </PrimaryButton>

        <button
          ref={buttonRef}
          id={`application-${application.id}`}
          className={classNames(
            'rounded-full border border-red-300 size-10 p-0 flex justify-center min-w-[40px]',
            'items-center text-red-500 disabled:text-secondary-400 disabled:border-secondary-200'
          )}
          onClick={handleButtonClick}
        >
          <ContentIcon name="block" className="size-[18px]" />
          {openMenu && (
            <DropDownMenu
              className="z-50 fixed"
              style={{
                left: `${menuPosition.x - 85}px`,
                top: `${menuPosition.y - 20}px`
              }}
              options={menuOptions}
              onBlur={() => setOpenMenu(false)}
            />
          )}
        </button>
      </div>
    </Scope>
  );
};

export default UnapprovedAttendanceRow;
